import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { BrandService, Brand } from "@/lib/brand-service";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  CheckCircleIcon, 
  SearchIcon, 
  ArrowRightIcon, 
  MapPinIcon, 
  CalendarIcon,
  FilterIcon
} from "lucide-react";
import BreadcrumbNavigation from "@/polymet/components/breadcrumb-navigation";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";

export default function BrandsDirectoryPage() {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [filteredBrands, setFilteredBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [countryFilter, setCountryFilter] = useState("all");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [countries, setCountries] = useState<string[]>([]);
  
  useEffect(() => {
    const loadBrands = async () => {
      try {
        setLoading(true);
        const brandService = new BrandService();
        const brandsData = await brandService.getBrands();
        
        setBrands(brandsData);
        setFilteredBrands(brandsData);
        
        // Extract unique countries
        const uniqueCountries = Array.from(new Set(
          brandsData
            .filter(brand => brand.country_of_origin)
            .map(brand => brand.country_of_origin as string)
        )).sort();
        
        setCountries(uniqueCountries);
      } catch (error) {
        console.error("Failed to load brands:", error);
      } finally {
        setLoading(false);
      }
    };
    
    loadBrands();
  }, []);
  
  // Apply filters when they change
  useEffect(() => {
    let result = [...brands];
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(brand => 
        brand.name.toLowerCase().includes(query) || 
        (brand.description && brand.description.toLowerCase().includes(query))
      );
    }
    
    // Apply country filter
    if (countryFilter && countryFilter !== 'all') {
      result = result.filter(brand => brand.country_of_origin === countryFilter);
    }
    
    // Apply verified filter
    if (verifiedOnly) {
      result = result.filter(brand => brand.verified);
    }
    
    setFilteredBrands(result);
  }, [brands, searchQuery, countryFilter, verifiedOnly]);
  
  // Reset all filters
  const resetFilters = () => {
    setSearchQuery("");
    setCountryFilter("all");
    setVerifiedOnly(false);
  };
  
  // Breadcrumb items
  const breadcrumbItems = [
    { name: "Home", href: "/" },
    { name: "Brands", href: "/brands" }
  ];
  
  return (
    <div className="container mx-auto px-4 py-8">
      {/* Breadcrumb Navigation */}
      <BreadcrumbNavigation items={breadcrumbItems} />
      
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mt-6 mb-8">
        <div>
          <h1 className="text-3xl font-bold">Machinery Brands</h1>
          <p className="text-gray-500 mt-1">Browse all machinery manufacturers and brands</p>
        </div>
      </div>
      
      {/* Filters */}
      <div className="bg-gray-50 p-4 rounded-lg mb-8">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-grow">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                placeholder="Search brands..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          
          {/* Country Filter */}
          <div className="w-full md:w-64">
            <Select
              value={countryFilter}
              onValueChange={setCountryFilter}
            >
              <SelectTrigger>
                <SelectValue placeholder="Filter by Country" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Countries</SelectItem>
                {countries.map(country => (
                  <SelectItem key={country} value={country}>{country}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Verified Filter */}
          <div className="flex items-center gap-2">
            <button
              className={`flex items-center gap-2 px-4 py-2 rounded-md border ${
                verifiedOnly 
                  ? "bg-blue-100 border-blue-300 text-blue-800" 
                  : "bg-white border-gray-300"
              }`}
              onClick={() => setVerifiedOnly(!verifiedOnly)}
            >
              <CheckCircleIcon className="h-4 w-4" />
              Verified Only
            </button>
          </div>
          
          {/* Reset Filters */}
          <div>
            <Button
              variant="outline"
              onClick={resetFilters}
              disabled={!searchQuery && (!countryFilter || countryFilter === 'all') && !verifiedOnly}
            >
              <FilterIcon className="h-4 w-4 mr-2" />
              Reset Filters
            </Button>
          </div>
        </div>
      </div>
      
      {/* Brands List */}
      {loading ? (
        <div className="text-center py-12">Loading brands...</div>
      ) : filteredBrands.length === 0 ? (
        <div className="text-center py-12 border rounded-md">
          <p className="text-gray-500 mb-4">No brands found matching your filters.</p>
          <Button variant="outline" onClick={resetFilters}>
            Reset Filters
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredBrands.map((brand) => (
            <Card key={brand.id} className="overflow-hidden">
              <div className="h-40 flex items-center justify-center bg-gray-50 border-b p-4">
                {brand.logo_url ? (
                  <img 
                    src={brand.logo_url} 
                    alt={brand.name} 
                    className="max-w-[80%] max-h-[80%] object-contain"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="text-2xl font-bold text-gray-500">
                      {(brand.name && typeof brand.name === 'string') ? brand.name.charAt(0) : 'B'}
                    </span>
                  </div>
                )}
              </div>
              
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold">{brand.name}</h2>
                  {brand.verified && (
                    <Badge variant="secondary" className="gap-1">
                      <CheckCircleIcon className="h-3.5 w-3.5" />
                      Verified
                    </Badge>
                  )}
                </div>
              </CardHeader>
              
              <CardContent className="space-y-2">
                {brand.description && (
                  <p className="text-gray-600 text-sm line-clamp-2">{brand.description}</p>
                )}
                
                <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                  {brand.country_of_origin && (
                    <div className="flex items-center">
                      <MapPinIcon className="h-3.5 w-3.5 mr-1" />
                      {brand.country_of_origin}
                    </div>
                  )}
                  {brand.founded_year && (
                    <div className="flex items-center">
                      <CalendarIcon className="h-3.5 w-3.5 mr-1" />
                      Est. {brand.founded_year}
                    </div>
                  )}
                </div>
              </CardContent>
              
              <CardFooter>
                <Button variant="outline" className="w-full" asChild>
                  <Link to={`/brands/${brand.slug}`}>
                    View Products
                    <ArrowRightIcon className="h-4 w-4 ml-2" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
